import { FolderIcon } from "@heroicons/react/24/outline";
import { GeschaeftspartnerLogo } from "@/components/GeschaeftspartnerLogo";
import { HeroHeaderLogo } from "@/components/dataTable";
import type { Order, ProjectRejectPayload } from "@/lib/projects";
import {
  getProjectCustomFieldSchemas,
  openOrderFolder,
  projectRejectSuccessMessage,
  projectWorkflowStatus,
  projectWorkflowStatusBadgeClass,
  rejectProject,
} from "@/lib/projects";
import {
  createSharePointStandardFolders,
  getSharePointStandardFolders,
  patchOrderReferenzFields,
} from "@/lib/projectOverview";
import { Button } from "@/components/ui/Button";
import { MetaGrid, MetaRow, OverviewCard } from "@/projectOverview/OverviewCard";
import { BestellnummerField, BestellnummerQuelleBadge } from "@/components/BestellnummerField";
import { LEITWEG_ID_HELP } from "@/lib/leitwegId";
import {
  buildLabelIndexFromSchemas,
  resolveCustomFieldByLabels,
} from "@/pages/projekte/hoursUsage";
import {
  auftragsartAppBadgeClass,
  resolveAuftragsartAppDisplay,
} from "@/pages/projekte/auftragsartApp";
import { useEffect, useMemo, useRef, useState } from "react";
import type { HeroCustomFieldSchema } from "@/lib/projects";
import { type CurrentUser, userHasGroup, userIsBuero } from "@/lib/auth";
import { DuplicateNummerButton } from "@/projectOverview/DuplicateNummerButton";
import { OrdnerJahrBadge } from "@/projectOverview/OrdnerJahrBadge";
import { isElectronWithOpenFolder } from "@/lib/electron";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { ProjectRejectDialog } from "@/projectOverview/ProjectRejectDialog";
import { ProjectKorrekturBanner } from "@/projectOverview/ProjectKorrekturBanner";
import { ProjectKorrekturDialog } from "@/projectOverview/ProjectKorrekturDialog";
import { ProjectExecutionClassControl } from "@/projectOverview/ProjectExecutionClassControl";
import { ProjectWorkflowStatusControl } from "@/projectOverview/ProjectWorkflowStatusControl";
import { userCanManageMontageunterlagen } from "@/projectOverview/permissions";
import { toast } from "@/lib/toast";
import { AnsprechpartnerContactCard } from "@/projectOverview/AnsprechpartnerContactCard";
import { CreatorContactCard } from "@/projectOverview/CreatorContactCard";

const HERO_PROJECT_BASE = "https://login.hero-software.de/partner/Projects/view/";

const INFO_LINK_CLASS =
  "inline-flex h-8 items-center justify-center gap-2 rounded-lg bg-card px-3 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque transition hover:bg-fill-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-separator-opaque focus-visible:ring-offset-2 focus-visible:ring-offset-grouped";

function canCreateMuendlichBeauftragt(project: Order): boolean {
  if (project.muendlich_beauftragt_at) return false;
  if (project.angebot_commission_status === "beauftragt") return false;
  if (project.angebot_commission_status === "abgelehnt") return false;
  if (project.commissioned_gd_document_id) return false;
  return true;
}

export function ProjectInfoSection({
  project,
  canEdit,
  canEditReferenz = true,
  canMuendlichBeauftragt = false,
  onEdit,
  onSaved,
  user = null,
}: {
  project: Order;
  canEdit: boolean;
  /** Bestellnummer / Kundenreferenz – standardmäßig für alle mit Projektzugriff. */
  canEditReferenz?: boolean;
  /** Mündliche Beauftragung aus den Projektinfos (wie bei Stundennachweisen). */
  canMuendlichBeauftragt?: boolean;
  onEdit: () => void;
  onSaved?: () => void;
  user?: CurrentUser | null;
}) {
  const [schemas, setSchemas] = useState<HeroCustomFieldSchema[]>([]);
  const [bestellnummer, setBestellnummer] = useState(String(project.bestellnummer || ""));
  const [kundenreferenz, setKundenreferenz] = useState(String(project.kundenreferenz || ""));
  const [leitwegId, setLeitwegId] = useState(String(project.leitweg_id || ""));
  const [savingReferenz, setSavingReferenz] = useState(false);
  const [referenzMessage, setReferenzMessage] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectBusy, setRejectBusy] = useState(false);
  const [korrekturOpen, setKorrekturOpen] = useState(false);
  const [structureMissing, setStructureMissing] = useState(false);
  const [structureBusy, setStructureBusy] = useState(false);
  const [structureHint, setStructureHint] = useState("");

  const { openMuendlichBeauftragt } = useTimesheetFlow();
  const workflow = projectWorkflowStatus(project);
  const isOpenStatus = workflow.key === "offen";
  const isBuero = userIsBuero(user);
  const showMuendlich =
    isOpenStatus && canMuendlichBeauftragt && canCreateMuendlichBeauftragt(project);
  const showReject = isBuero && workflow.key !== "abgelehnt";
  const showKorrektur = isBuero && !project.merged_into_auftrag_id;

  useEffect(() => {
    void getProjectCustomFieldSchemas().then(setSchemas).catch(() => setSchemas([]));
  }, []);

  const serverBestellnummer = String(project.bestellnummer || "");
  const serverKundenreferenz = String(project.kundenreferenz || "");
  const serverLeitwegId = String(project.leitweg_id || "");
  const seenBestellnummer = useRef(serverBestellnummer);
  const seenKundenreferenz = useRef(serverKundenreferenz);
  const seenLeitwegId = useRef(serverLeitwegId);
  const seenProjectId = useRef(project.id);

  useEffect(() => {
    const switched = seenProjectId.current !== project.id;
    seenProjectId.current = project.id;
    setBestellnummer((current) => {
      const previous = seenBestellnummer.current;
      seenBestellnummer.current = serverBestellnummer;
      if (
        !switched &&
        current.trim() !== previous.trim() &&
        current.trim() !== serverBestellnummer.trim()
      ) {
        return current;
      }
      return serverBestellnummer;
    });
    setKundenreferenz((current) => {
      const previous = seenKundenreferenz.current;
      seenKundenreferenz.current = serverKundenreferenz;
      if (
        !switched &&
        current.trim() !== previous.trim() &&
        current.trim() !== serverKundenreferenz.trim()
      ) {
        return current;
      }
      return serverKundenreferenz;
    });
    setLeitwegId((current) => {
      const previous = seenLeitwegId.current;
      seenLeitwegId.current = serverLeitwegId;
      if (
        !switched &&
        current.trim() !== previous.trim() &&
        current.trim() !== serverLeitwegId.trim()
      ) {
        return current;
      }
      return serverLeitwegId;
    });
    if (switched) setReferenzMessage("");
  }, [project.id, serverBestellnummer, serverKundenreferenz, serverLeitwegId]);

  const canManageFolders = userIsBuero(user);
  const hasSharepointFolder = Boolean(project.sharepoint_folder_id);

  useEffect(() => {
    if (!canManageFolders || !hasSharepointFolder) {
      setStructureMissing(false);
      setStructureHint("");
      return;
    }
    let cancelled = false;
    void getSharePointStandardFolders(project.id)
      .then((status) => {
        if (cancelled) return;
        setStructureMissing(!status.complete);
        setStructureHint(
          status.complete
            ? ""
            : status.missing.length
              ? `Fehlt: ${status.missing.join(", ")}`
              : "",
        );
      })
      .catch(() => {
        if (!cancelled) setStructureMissing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canManageFolders, hasSharepointFolder, project.id, project.sharepoint_folder_id]);

  const auftragsart = useMemo(() => {
    const labelIndex = buildLabelIndexFromSchemas(schemas);
    const heroValue = resolveCustomFieldByLabels(project, ["Auftragsart"], labelIndex);
    return resolveAuftragsartAppDisplay(project.auftragsart_app, heroValue);
  }, [project, schemas]);

  const heroId = project.hero_project_id;
  const showFolderOpen = userIsBuero(user) && isElectronWithOpenFolder();
  const hasFolder = Boolean(
    project.ordnerpfad ||
      project.sharepoint_path ||
      project.sharepoint_folder_id ||
      project.has_folder,
  );
  const referenzDirty =
    bestellnummer.trim() !== String(project.bestellnummer || "").trim() ||
    kundenreferenz.trim() !== String(project.kundenreferenz || "").trim() ||
    leitwegId.trim() !== String(project.leitweg_id || "").trim();

  async function handleSaveReferenz() {
    setSavingReferenz(true);
    setReferenzMessage("");
    try {
      await patchOrderReferenzFields(project.id, {
        bestellnummer: bestellnummer.trim() || null,
        kundenreferenz: kundenreferenz.trim() || null,
        leitweg_id: leitwegId.trim() || null,
      });
      setReferenzMessage("Gespeichert.");
      onSaved?.();
    } catch (err) {
      setReferenzMessage(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingReferenz(false);
    }
  }

  async function handleCreateStandardFolders() {
    setStructureBusy(true);
    setStructureHint("");
    try {
      const status = await createSharePointStandardFolders(project.id);
      setStructureMissing(!status.complete);
      if (status.complete) {
        toast.success("Ordnerstruktur angelegt.");
      } else {
        setStructureHint(
          status.missing.length ? `Noch fehlend: ${status.missing.join(", ")}` : "Unvollständig.",
        );
        toast.error("Ordnerstruktur ist noch unvollständig.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Ordnerstruktur konnte nicht angelegt werden.";
      setStructureHint(message);
      toast.error(message);
    } finally {
      setStructureBusy(false);
    }
  }

  async function handleReject(payload: ProjectRejectPayload) {
    setRejectBusy(true);
    try {
      const result = await rejectProject(project.id, payload);
      toast.success(projectRejectSuccessMessage(result));
      setRejectOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Ablehnung fehlgeschlagen.");
    } finally {
      setRejectBusy(false);
    }
  }

  const hasAnsprechpartner = Boolean(
    project.ansprechpartner_id || String(project.ansprechpartner || "").trim(),
  );
  const showStructureAction = canManageFolders && hasSharepointFolder && structureMissing;
  const showRenumber = userHasGroup(user, "App-Admins");
  const showActions = Boolean(
    heroId ||
      showFolderOpen ||
      showStructureAction ||
      canEdit ||
      showMuendlich ||
      showReject ||
      showKorrektur ||
      showRenumber,
  );

  const customerName = String(project.kunde || project.gp_firmenname || "").trim();
  const showCustomerLogo = Boolean(customerName && customerName !== "—");

  return (
    <OverviewCard title="Projektinfos">
      <ProjectKorrekturBanner project={project} />
      <div className="eh-po-info">
        <div className="eh-po-info-head">
          {showCustomerLogo ? (
            <GeschaeftspartnerLogo
              partnerId={project.geschaeftspartner_id}
              name={customerName}
              hasLogo={project.gp_has_logo}
              logoUpdatedAt={project.gp_logo_updated_at}
              className="mt-0.5 h-11 w-11 rounded-xl text-xs"
            />
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="eh-po-info-nr">
              {project.auftragsnummer || "—"}
              <OrdnerJahrBadge project={project} onMoved={onSaved} />
            </p>
            {project.kunde ? <p className="eh-po-info-sub">{project.kunde}</p> : null}
          </div>
          <div className="eh-po-info-badges">
            <ProjectWorkflowStatusControl
              project={project}
              canEdit={isBuero}
              variant="badge"
              onSaved={onSaved}
            />
            {workflow.secondary ? (
              <span className={projectWorkflowStatusBadgeClass(workflow.secondary.key)}>
                {workflow.secondary.label}
              </span>
            ) : null}
            {auftragsart?.kind ? (
              <span className={auftragsartAppBadgeClass(auftragsart.kind)}>{auftragsart.label}</span>
            ) : auftragsart?.label ? (
              <span className="eh-wf-badge eh-wf-badge--offen">{auftragsart.label}</span>
            ) : null}
            {project.workflow_status_manual ? (
              <span className="text-[11px] font-medium text-label-tertiary">manuell</span>
            ) : null}
          </div>
        </div>

        {hasAnsprechpartner ? (
          <AnsprechpartnerContactCard
            orderId={project.id}
            ansprechpartnerId={project.ansprechpartner_id ?? null}
            fallbackName={project.ansprechpartner}
            geschaeftspartnerId={project.geschaeftspartner_id ?? null}
            heroCustomerId={project.hero_customer_id ?? null}
            canEdit={canEdit}
            onSaved={onSaved}
          />
        ) : null}

        <CreatorContactCard
          orderId={project.id}
          login={project.created_by}
          fallbackName={project.created_by_name || project.created_by}
          createdAt={project.created_at}
        />

        <MetaGrid>
          {userCanManageMontageunterlagen(user) ||
          String(project.execution_class || "").trim() ? (
            <>
              <dt>Execution Class</dt>
              <dd>
                <ProjectExecutionClassControl
                  project={project}
                  canEdit={userCanManageMontageunterlagen(user)}
                  onSaved={onSaved}
                  hideLabel
                />
              </dd>
            </>
          ) : null}
          {hasAnsprechpartner ? null : <MetaRow label="Ansprechpartner" value={null} />}
          <MetaRow label="Ort" value={project.ort} />
          {project.ort_zusatz?.trim() ? (
            <MetaRow label="Ort Zusatz" value={project.ort_zusatz} />
          ) : null}
          <MetaRow label="Beschreibung" value={project.beschreibung} />
          {canEditReferenz ? null : (
            <>
              <dt>Bestellnummer</dt>
              <dd className="flex flex-wrap items-center gap-2">
                <span>{project.bestellnummer || "—"}</span>
                <BestellnummerQuelleBadge quelle={project.bestellnummer_quelle} />
              </dd>
              <MetaRow label="Kundenreferenz" value={project.kundenreferenz} />
              <MetaRow label="Leitweg-ID" value={project.leitweg_id} />
            </>
          )}
        </MetaGrid>

        {canEditReferenz ? (
          <div className="eh-po-info-panel">
            <p className="eh-po-info-panel-title">Referenzen</p>
            <BestellnummerField
              id={`po-bestellnummer-${project.id}`}
              value={bestellnummer}
              onChange={setBestellnummer}
              disabled={savingReferenz}
              quelle={project.bestellnummer_quelle}
              regelWert={project.bestellnummer_regel_wert}
              compact
              onApplyRegel={() =>
                setBestellnummer(String(project.bestellnummer_regel_wert || "").trim())
              }
            />
            <div className="eh-po-info-field">
              <label className="eh-label mb-0" htmlFor={`po-kundenreferenz-${project.id}`}>
                Kundenreferenz
              </label>
              <input
                id={`po-kundenreferenz-${project.id}`}
                type="text"
                className="eh-input w-full"
                value={kundenreferenz}
                disabled={savingReferenz}
                onChange={(e) => setKundenreferenz(e.target.value)}
                placeholder="Kundenreferenz …"
              />
            </div>
            <div className="eh-po-info-field">
              <label className="eh-label mb-0" htmlFor={`po-leitweg-${project.id}`}>
                Leitweg-ID
              </label>
              <input
                id={`po-leitweg-${project.id}`}
                type="text"
                className="eh-input w-full font-mono"
                value={leitwegId}
                disabled={savingReferenz}
                onChange={(e) => setLeitwegId(e.target.value)}
                placeholder="z. B. 04011000-1234512345-06"
                spellCheck={false}
              />
              <p className="mt-1 text-xs text-label-tertiary">{LEITWEG_ID_HELP}</p>
            </div>
            <div className="eh-po-info-panel-foot">
              {referenzMessage ? <p className="eh-po-info-panel-msg">{referenzMessage}</p> : null}
              <Button
                type="button"
                size="sm"
                variant={referenzDirty ? "primary" : "secondary"}
                loading={savingReferenz}
                disabled={!referenzDirty}
                onClick={() => void handleSaveReferenz()}
              >
                Speichern
              </Button>
            </div>
          </div>
        ) : null}

        {showActions ? (
          <div className="eh-po-info-actions-block">
            <div className="eh-po-info-actions">
              {canEdit ? (
                <Button type="button" size="sm" onClick={onEdit}>
                  Bearbeiten
                </Button>
              ) : null}
              {heroId ? (
                <a
                  href={`${HERO_PROJECT_BASE}${encodeURIComponent(String(heroId))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={INFO_LINK_CLASS}
                >
                  <HeroHeaderLogo className="h-4 w-4 object-contain" />
                  Hero
                </a>
              ) : null}
              {showFolderOpen ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  title={hasFolder ? "Ordner öffnen" : "Kein Projektordner hinterlegt"}
                  disabled={!hasFolder}
                  leftIcon={<FolderIcon className="h-4 w-4" />}
                  onClick={() => void openOrderFolder(project)}
                >
                  Ordner
                </Button>
              ) : null}
              {showStructureAction ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  loading={structureBusy}
                  title={structureHint || "Standard-Unterordner im SharePoint-Projektordner anlegen"}
                  onClick={() => void handleCreateStandardFolders()}
                >
                  Ordnerstruktur
                </Button>
              ) : null}
              {showMuendlich ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => openMuendlichBeauftragt(project)}
                >
                  Mündlich beauftragt
                </Button>
              ) : null}
              {showReject ? (
                <Button type="button" size="sm" variant="danger" onClick={() => setRejectOpen(true)}>
                  Ablehnen
                </Button>
              ) : null}
              {showRenumber ? (
                <DuplicateNummerButton projectId={project.id} onDone={onSaved} />
              ) : null}
            </div>
            {showStructureAction && structureHint ? (
              <p className="eh-po-info-note">{structureHint}</p>
            ) : null}
            {showKorrektur ? (
              <p className="eh-po-info-rare">
                <button type="button" onClick={() => setKorrekturOpen(true)}>
                  Zusammenführen
                </button>
                <span>Nur wenn dieses Projekt doppelt angelegt wurde.</span>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      <ProjectRejectDialog
        open={rejectOpen}
        projectId={project.id}
        auftragsnummer={project.auftragsnummer}
        loading={rejectBusy}
        onClose={() => {
          if (!rejectBusy) setRejectOpen(false);
        }}
        onConfirm={(payload) => void handleReject(payload)}
      />
      <ProjectKorrekturDialog
        open={korrekturOpen}
        project={project}
        onClose={() => setKorrekturOpen(false)}
        onSaved={onSaved}
      />
    </OverviewCard>
  );
}
